import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils.js";

const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const tooltipContentVariants = cva(
  // The open/close animation lives in tokens.css as .tooltip-content.
  "tooltip-content z-[10060] rounded-md border shadow-md",
  {
    variants: {
      tone: {
        default: "border-surface-200 bg-surface-0 text-surface-800",
        inverted: "border-surface-800 bg-surface-900 text-surface-0",
      },
      size: {
        sm: "px-2 py-1 text-[11px]",
        md: "px-2.5 py-1.5 text-xs",
      },
    },
    defaultVariants: { tone: "default", size: "md" },
  },
);

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & VariantProps<typeof tooltipContentVariants>
>(({ className, tone, size, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(tooltipContentVariants({ tone, size }), className)}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export interface TooltipProps extends VariantProps<typeof tooltipContentVariants> {
  /** What the tooltip says. Nothing is rendered when this is empty. */
  content: React.ReactNode;
  children: React.ReactElement;
  side?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>["side"];
  align?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>["align"];
  /** Gap in px between the trigger and the tooltip. */
  sideOffset?: number;
  /** ms before it opens. 0 (the default) shows it instantly on hover. */
  delayDuration?: number;
  /** Caps the width so a long string wraps instead of running off-screen. */
  maxWidth?: number | string;
  contentClassName?: string;
}

/** The one-call form: wrap any element to give it a themed hover/focus tooltip.
 * Use this instead of a `title` attribute, which is delayed and unstyleable. */
export function Tooltip({
  content, children, side = "top", align = "center", sideOffset = 6,
  delayDuration = 0, maxWidth = 320, contentClassName, tone, size,
}: TooltipProps) {
  if (content == null || content === "" || content === false) return children;
  return (
    <TooltipProvider delayDuration={delayDuration} skipDelayDuration={0}>
      <TooltipRoot>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          sideOffset={sideOffset}
          tone={tone}
          size={size}
          className={cn("whitespace-normal break-words", contentClassName)}
          // Runtime cap: the caller's width can't be expressed as a static class.
          style={{ maxWidth }}
        >
          {content}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
}

export { TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent, tooltipContentVariants };
